import { DateValue } from "../types";
import { dateStringToDate, isFilledDate, isRangeMode } from "../utils";
import { RU_LOCALE } from "./constants";
import { Nullable } from "./types";

export const formatDateToLocaleString = (
  date: Nullable<Date>,
  locale: string = RU_LOCALE
) => (date ? date.toLocaleDateString(locale) : "");

export const isValidDateString = (date: string) => {
  const parsedDate = Date.parse(date);

  return isFilledDate(date) && !Number.isNaN(parsedDate);
};

export const hasDateLimit = (
  checkedDate: Nullable<Date>,
  minDate: Nullable<Date>,
  maxDate: Nullable<Date>
) => {
  if (!checkedDate) {
    return false;
  }

  if (minDate && !maxDate) {
    return checkedDate < minDate;
  }

  if (maxDate && !minDate) {
    return checkedDate > maxDate;
  }

  if (minDate && maxDate) {
    return checkedDate < minDate || checkedDate > maxDate;
  }

  return false;
};

export const isValidDateValue = (
  value: DateValue,
  minDate: Nullable<Date>,
  maxDate: Nullable<Date>
) => {
  if (isRangeMode(value)) {
    return (
      value.some(isValidDateString) &&
      value.some(
        (isoDate) => !hasDateLimit(dateStringToDate(isoDate), minDate, maxDate)
      )
    );
  }

  return (
    isValidDateString(value) &&
    !hasDateLimit(dateStringToDate(value), minDate, maxDate)
  );
};

/**
 * Функция, возвращающая дату, относительно которой отображается календарь, учитывая, что она не может выходить за пределы ограничивающего диапазона.
 *
 * Пример: getViewDate("2022-06-22", "2022-09-22", "2022-12-22") -> Thu Sep 22 2022 00:00:00 GMT+0300 (Москва, стандартное время)
 *         getViewDate("", "2022-09-22", "") -> Thu Sep 22 2022 00:00:00 GMT+0300 (Москва, стандартное время)
 *         getViewDate("2022-06-22", "", "") -> Wed Jun 22 2022 00:00:00 GMT+0300 (Москва, стандартное время)
 */
export const getViewDate = (
  date: DateValue,
  minDate: Nullable<Date>,
  maxDate: Nullable<Date>
) => {
  const viewDate = isRangeMode(date) ? date[0] : date;
  const currentDate =
    (isValidDateValue(viewDate, null, null) && dateStringToDate(viewDate)) ||
    new Date();

  const currentDateMs = currentDate.valueOf();

  if (minDate && minDate?.valueOf() > currentDateMs) {
    return minDate;
  }

  if (maxDate && maxDate?.valueOf() < currentDateMs) {
    return maxDate;
  }

  return currentDate;
};
